import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSubscriptionsStore } from '../../stores/useSubscriptionsStore';
import { Search, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';

function getClienteDisplayName(c: { nombre?: string; apellido?: string; nombre_completo?: string }): string {
  return c.nombre_completo || [c.nombre, c.apellido].filter(Boolean).join(' ').trim() || '—';
}

function getProductoNombre(sub: { producto?: unknown }): string {
  if (!sub?.producto) return '—';
  const p = Array.isArray(sub.producto) ? sub.producto[0] : sub.producto;
  return (p as { nombre?: string })?.nombre ?? '—';
}

// Safari: full-height layout (100dvh / -webkit-fill-available)
const checkInPageStyle = (
  <style>{`
    .check-in-page {
      min-height: 100vh;
      min-height: 100dvh;
      min-height: -webkit-fill-available;
    }
  `}</style>
);

export function PublicCheckIn() {
  const { slug } = useParams<{ slug: string }>();

  const [step, setStep] = useState<'search' | 'select-subscription' | 'success'>('search');
  const [empresa, setEmpresa] = useState<{ id: string; nombre: string; logo_url?: string; slug?: string } | null>(null);
  const [empresaLoadError, setEmpresaLoadError] = useState(false);
  const [empresaLoading, setEmpresaLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchRanWithNoActiveSubs, setSearchRanWithNoActiveSubs] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [customerSubscriptions, setCustomerSubscriptions] = useState<any[]>([]);
  const [selectedSubscription, setSelectedSubscription] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { recordAttendance } = useSubscriptionsStore();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadEmpresa();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadEmpresa = async () => {
    if (!slug) {
      setEmpresaLoading(false);
      return;
    }
    setEmpresaLoading(true);
    setEmpresaLoadError(false);
    setEmpresa(null);

    const { data, error } = await supabase
      .from('empresas')
      .select('id, nombre, logo_url, slug')
      .eq('slug', slug)
      .maybeSingle();

    setEmpresaLoading(false);
    if (error) {
      console.error('Check-in: load empresa by slug failed', error.message, error.details);
      setEmpresaLoadError(true);
      return;
    }
    setEmpresa(data ?? null);
    if (!data) setEmpresaLoadError(true);
  };

  const handleSearch = async () => {
    if (!searchTerm.trim() || !empresa?.id) return;

    setIsLoading(true);
    setSearchResults([]);
    setSearchRanWithNoActiveSubs(false);

    const term = searchTerm.trim();
    let clientes: { id: string; nombre?: string; apellido?: string; telefono?: string }[] = [];

    try {
      // 1) Try RPC first (works when migration is applied; SECURITY DEFINER so anon gets results)
      const { data: clientesData, error: clientesError } = await supabase.rpc('search_checkin_clientes', {
        p_empresa_id: empresa.id,
        p_search_term: term,
      });

      if (!clientesError && clientesData != null) {
        clientes = (Array.isArray(clientesData) ? clientesData : []) as typeof clientes;
      }

      // 2) Fallback: if RPC failed (e.g. function not deployed or Safari) use simple per-column queries and merge (avoids .or() URL encoding issues in Safari)
      if (clientes.length === 0) {
        const pattern = `%${term}%`;
        const limit = 20;
        const selectCols = 'id, nombre, apellido, telefono';
        const [byNombre, byApellido, byTelefono, byEmail] = await Promise.all([
          supabase.from('clientes').select(selectCols).eq('empresa_id', empresa.id).ilike('nombre', pattern).limit(limit),
          supabase.from('clientes').select(selectCols).eq('empresa_id', empresa.id).ilike('apellido', pattern).limit(limit),
          supabase.from('clientes').select(selectCols).eq('empresa_id', empresa.id).ilike('telefono', pattern).limit(limit),
          supabase.from('clientes').select(selectCols).eq('empresa_id', empresa.id).ilike('email', pattern).limit(limit),
        ]);
        const seen = new Set<string>();
        const merged: typeof clientes = [];
        for (const res of [byNombre, byApellido, byTelefono, byEmail]) {
          if (res.error) continue;
          for (const row of res.data ?? []) {
            if (seen.has(row.id)) continue;
            seen.add(row.id);
            merged.push(row as (typeof clientes)[0]);
            if (merged.length >= limit) break;
          }
          if (merged.length >= limit) break;
        }
        if (merged.length) clientes = merged;
        if (clientesError && clientes.length) {
          console.warn('Check-in RPC failed, used direct query fallback:', clientesError.message);
        }
      }

      if (clientes.length === 0) {
        setIsLoading(false);
        return;
      }

      // 3) Get active member_subscriptions for those clientes
      const clienteIds = clientes.map((c) => c.id);
      const { data: subsData, error: subsError } = await supabase
        .from('member_subscriptions')
        .select('id, cliente_id')
        .eq('empresa_id', empresa.id)
        .eq('status', 'active')
        .in('cliente_id', clienteIds);

      if (subsError) {
        console.warn('Check-in: member_subscriptions query failed', subsError);
      }

      const activeClienteIds = new Set((subsData ?? []).map((s: { cliente_id: string }) => s.cliente_id));
      const activeCustomers = clientes.filter((c) => activeClienteIds.has(c.id));

      // Same as manual check-in: only show people with an active subscription (they are the only ones who can check in)
      setSearchResults(activeCustomers);
      setSearchRanWithNoActiveSubs(activeCustomers.length === 0 && clientes.length > 0);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectCustomer = async (customer: any) => {
    setSelectedCustomer(customer);

    const { data: subscriptions } = await supabase
      .from('member_subscriptions')
      .select(
        `
        *,
        producto:productos(nombre, clases_incluidas)
      `
      )
      .eq('cliente_id', customer.id)
      .eq('status', 'active');

    setCustomerSubscriptions(subscriptions || []);

    if (subscriptions?.length === 1) {
      setSelectedSubscription(subscriptions[0]);
    } else {
      setSelectedSubscription(null);
    }

    setStep('select-subscription');
  };

  const handleConfirmCheckIn = async () => {
    if (!selectedCustomer || !selectedSubscription || !empresa) return;

    setIsLoading(true);

    try {
      const record = await recordAttendance({
        empresa_id: empresa.id,
        subscription_id: selectedSubscription.id,
        cliente_id: selectedCustomer.id,
        check_in_method: 'qr_self',
      });

      if (!record) throw new Error('Error al registrar');

      setStep('success');

      setTimeout(() => {
        setStep('search');
        setSearchTerm('');
        setSearchResults([]);
        setSelectedCustomer(null);
        setCustomerSubscriptions([]);
        setSelectedSubscription(null);
      }, 5000);
    } catch (error) {
      console.error('Check-in error:', error);
      alert('Error al registrar entrada');
    } finally {
      setIsLoading(false);
    }
  };

  if (empresaLoading && slug) {
    return (
      <>
        {checkInPageStyle}
        <div className="check-in-page min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4" />
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
      </>
    );
  }

  if ((empresaLoadError || (slug && !empresa)) && !empresaLoading) {
    return (
      <>
        {checkInPageStyle}
        <div className="check-in-page min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Empresa no encontrada</h2>
          <p className="text-gray-600">Verifica el enlace del código QR.</p>
        </div>
      </div>
      </>
    );
  }

  if (!empresa) return null;

  if (step === 'search') {
    return (
      <>
        {checkInPageStyle}
        <div className="check-in-page min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 p-4">
        <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full">
          {empresa?.logo_url && (
            <img
              src={empresa.logo_url}
              alt={empresa.nombre}
              className="h-16 mx-auto mb-6 object-contain"
            />
          )}

          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              ¡Bienvenido a {empresa?.nombre}!
            </h1>
            <p className="text-gray-600">Registra tu entrada</p>
          </div>

          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Busca por nombre, teléfono o correo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>

            <button
              onClick={handleSearch}
              disabled={isLoading || !searchTerm.trim()}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Buscando...' : 'Buscar'}
            </button>
          </div>

          {searchResults.length > 0 && (
            <div className="mt-6 space-y-2 max-h-60 overflow-y-auto">
              {searchResults.map((customer) => (
                <button
                  key={customer.id}
                  type="button"
                  onClick={() => handleSelectCustomer(customer)}
                  className="w-full text-left p-4 bg-gray-50 rounded-lg border hover:bg-blue-50 hover:border-blue-300"
                >
                  <p className="font-medium text-gray-900">{getClienteDisplayName(customer)}</p>
                  <p className="text-sm text-gray-600">{customer.telefono}</p>
                </button>
              ))}
            </div>
          )}

          {searchResults.length === 0 && searchTerm && !isLoading && (
            <p className="mt-4 text-center text-gray-500 text-sm">
              {searchRanWithNoActiveSubs
                ? 'Se encontraron clientes pero ninguno tiene membresía activa. Contacta a la empresa para renovar.'
                : 'No se encontraron clientes. Prueba con nombre, apellido, teléfono o correo.'}
            </p>
          )}
        </div>
      </div>
      </>
    );
  }

  if (step === 'select-subscription') {
    const hasNoSubscriptions = !customerSubscriptions?.length;

    return (
      <>
        {checkInPageStyle}
        <div className="check-in-page min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 p-4">
        <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full">
          {empresa?.logo_url && (
            <img
              src={empresa.logo_url}
              alt={empresa.nombre}
              className="h-16 mx-auto mb-6 object-contain"
            />
          )}

          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              ¡Hola {selectedCustomer ? getClienteDisplayName(selectedCustomer).split(' ')[0] : ''}!
            </h2>
            <p className="text-gray-600">
              {hasNoSubscriptions ? 'No tienes una membresía activa' : '¿A qué clase vienes hoy?'}
            </p>
          </div>

          {hasNoSubscriptions ? (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-center text-amber-800 text-sm">
              Para registrar tu entrada necesitas una membresía o plan activo. Contacta a la empresa para inscribirte o renovar.
            </div>
          ) : (
            <div className="space-y-3 mb-6">
              {customerSubscriptions.map((subscription) => (
                <button
                  key={subscription.id}
                  type="button"
                  onClick={() => setSelectedSubscription(subscription)}
                  className={`w-full text-left p-4 rounded-lg border-2 ${
                    selectedSubscription?.id === subscription.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-gray-50 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {getProductoNombre(subscription)}
                      </p>
                      {subscription.classes_remaining !== null && (
                        <p className="text-sm text-blue-600 mt-1">
                          {subscription.classes_remaining} clases restantes
                        </p>
                      )}
                      {subscription.classes_remaining === null && (
                        <p className="text-sm text-green-600 mt-1">Clases ilimitadas</p>
                      )}
                    </div>
                    {selectedSubscription?.id === subscription.id && (
                      <Check className="h-6 w-6 text-blue-600 flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                setStep('search');
                setSelectedCustomer(null);
                setCustomerSubscriptions([]);
                setSelectedSubscription(null);
              }}
              className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-50"
            >
              {hasNoSubscriptions ? 'Volver' : 'Cancelar'}
            </button>
            {!hasNoSubscriptions && (
              <button
                type="button"
                onClick={handleConfirmCheckIn}
                disabled={!selectedSubscription || isLoading}
                className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50"
              >
                {isLoading ? 'Registrando...' : 'Confirmar Entrada'}
              </button>
            )}
          </div>
        </div>
      </div>
      </>
    );
  }

  if (step === 'success') {
    const classesRemaining =
      selectedSubscription?.classes_remaining !== null
        ? (selectedSubscription?.classes_remaining || 0) - 1
        : null;

    return (
      <>
        {checkInPageStyle}
        <div className="check-in-page min-h-screen flex items-center justify-center bg-green-500 p-4">
        <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <Check className="h-12 w-12 text-green-600" />
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-2">¡Entrada Registrada!</h2>

          <p className="text-xl text-gray-700 mb-1">
            {selectedCustomer ? getClienteDisplayName(selectedCustomer) : ''}
          </p>

          <p className="text-gray-600 mb-4">{selectedSubscription ? getProductoNombre(selectedSubscription) : ''}</p>

          <p className="text-gray-600 text-lg mb-1">
            {new Date().toLocaleTimeString('es-CO', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>

          {classesRemaining !== null && (
            <p className="text-gray-700 font-medium mt-4">Clases restantes: {classesRemaining}</p>
          )}

          <p className="text-gray-500 text-sm mt-6">¡Que tengas un excelente entrenamiento! 💪</p>

          <p className="text-xs text-gray-400 mt-4">Redirigiendo automáticamente...</p>
        </div>
      </div>
      </>
    );
  }

  return null;
}
